import { ArrowRight, Lock } from "lucide-react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MyFortAgentItem } from "@/api/types";
import { C } from "@/components/modal/home";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS } from "@/config/env";
import {
  formatEtherWithDecimals,
  formatThousands,
  formatUsdPrefix,
} from "@/lib/utils";
import { AGENT_IMAGE_URL_INACTIVE } from "./constants";

export function HomeMyAgentInactiveCard({
  agent,
  onGoManage,
}: {
  agent: MyFortAgentItem;
  onGoManage: () => void;
}) {
  const { t } = useTranslation();
  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const purchaseDateStr = dayjs(agent.createdAt).format("YYYY-MM-DD");
  const unitPrice = formatThousands(
    formatEtherWithDecimals(agent.priceBasisUsdt, 0),
    0
  );
  const exitCap = formatThousands(
    formatEtherWithDecimals(agent.staticCapUsdt, 0),
    0
  );

  const benefitCells = [
    {
      label: t("homePage.benefit.custodyBoost"),
      value: `${agent.aiManageAddSpeedBp / 100}%`,
      color: C.primary,
    },
    {
      label: t("homePage.benefit.releaseMultiple"),
      value: `${agent.exitMultiple / 100}x`,
      color: C.green,
    },
    {
      label: t("homePage.benefit.aifToken", {
        tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
      }),
      value: formatUsdPrefix(
        formatThousands(formatEtherWithDecimals(agent.aifValueUsdt, 0), 0)
      ),
      color: C.amber,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl overflow-hidden relative"
      style={{
        background:
          "linear-gradient(135deg, rgba(212,165,116,0.03) 0%, #0c1020 40%, #080c18 100%)",
        border: "1px solid rgba(212,165,116,0.07)",
        boxShadow: "0 2px 20px rgba(212,165,116,0.02)",
      }}
    >
      <div
        className="h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 10%, rgba(212,165,116,0.08) 50%, transparent 90%)",
        }}
      />
      <div className="flex" style={{ minHeight: 0 }}>
        <div
          className="relative flex flex-col items-center justify-center overflow-hidden"
          style={{
            width: "42%",
            minHeight: "200px",
            background: "rgba(212,165,116,0.01)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full blur-[40px]"
              style={{ background: "rgba(100,116,139,0.04)" }}
            />
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-28 h-28">
            <div
              className="w-full h-full rounded-full"
              style={{ border: "1px solid rgba(100,116,139,0.05)" }}
            />
          </div>
          <div
            className="relative z-10 flex items-center justify-center"
            style={{ width: "90%", height: "90%" }}
          >
            <img
              src={AGENT_IMAGE_URL_INACTIVE}
              alt="AI Agent"
              className="w-full h-full object-contain"
              style={{ opacity: 0.45 }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  background: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(212,165,116,0.20)",
                }}
              >
                <Lock size={14} style={{ color: "#d4a574" }} />
              </div>
            </div>
          </div>
          <div className="relative w-14 h-1 -mt-0.5">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "radial-gradient(ellipse, rgba(100,116,139,0.10) 0%, transparent 70%)",
              }}
            />
          </div>
        </div>

        <div
          className="flex-1 py-3.5 pr-3.5 pl-1 space-y-2"
          style={{ minWidth: 0 }}
        >
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="font-black text-base tracking-wide"
                style={{ color: C.muted }}
              >
                {agent.name}
              </span>
              <span
                className="text-[10px] px-1.5 py-[2px] rounded font-semibold"
                style={{
                  background: `${tagColor}12`,
                  color: tagColor,
                  border: `1px solid ${tagColor}20`,
                }}
              >
                {tierLabel}
              </span>
              <span
                className="flex items-center gap-1 px-1.5 py-[2px] rounded text-[10px] font-bold ml-auto"
                style={{
                  background: "rgba(212,165,116,0.06)",
                  color: "#d4a574",
                  border: "1px solid rgba(212,165,116,0.10)",
                }}
              >
                <Lock size={8} />
                {t("homePage.myAgentCard.statusPending")}
              </span>
            </div>
            <div className="mt-0.5 text-[10px]" style={{ color: C.dim }}>
              {t("homePage.myAgentHomeInactive.purchasedOn", {
                date: purchaseDateStr,
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {benefitCells.map(b => (
              <div
                key={b.label}
                className="text-center py-1.5 rounded-lg basis-[calc(33.333%-0.375rem)] min-w-[58px] flex-1"
                style={{
                  background: `${b.color}06`,
                  border: `1px solid ${b.color}08`,
                }}
              >
                <div className="text-[10px]" style={{ color: C.dim }}>
                  {b.label}
                </div>
                <div
                  className="text-[10px] font-bold mono-num"
                  style={{ color: b.color }}
                >
                  {b.value}
                </div>
              </div>
            ))}
            {agent.giftGiveLevel > 0 ? (
              <div
                className="text-center py-1.5 rounded-lg basis-[calc(33.333%-0.375rem)] min-w-[58px] flex-1"
                style={{
                  background: `${C.cyan}06`,
                  border: `1px solid ${C.cyan}08`,
                }}
              >
                <div className="text-[10px]" style={{ color: C.dim }}>
                  {t("homePage.benefit.teamLevel")}
                </div>
                <div
                  className="text-[10px] font-bold mono-num"
                  style={{ color: C.cyan }}
                >
                  {`C${agent.giftGiveLevel}`}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className="rounded-lg p-2 space-y-1"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${C.border}`,
            }}
          >
            <div className="flex justify-between text-[10px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentHomeInactive.unitPrice")}
              </span>
              <span className="mono-num font-semibold">${unitPrice}</span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentInactiveDetailDialog.exitCap")}
              </span>
              <span
                className="mono-num font-semibold"
                style={{ color: C.green }}
              >
                ${exitCap}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="w-full py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
            style={{
              background: `${C.primary}10`,
              color: C.primary,
              border: `1px solid ${C.primary}20`,
            }}
            onClick={onGoManage}
          >
            <ArrowRight size={12} />
            {t("homePage.myAgents.goManage")}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
